/**
 * PepDesignationCard — FSR "bank-identified Politically Exposed Person (PEP)"
 * designation, rendered in the Verify section beside OFAC + the LexisNexis
 * Fraud Screening (Bill 2026-06-17).
 *
 * Behavior is admin-configurable via GET /v3/config/pep (PepConfig):
 *   - enabled       → when false this card renders NOTHING (gate inert).
 *   - helperText    → the "More info" wording shown to the FSR.
 *   - enforcementMode / deferredDays → honored server-side at commit
 *     (CartCommitService); this card only captures the designation.
 *
 * The designation persists on `person.isPep` (round-trips via the cart people
 * JSON). It is v3-internal — NOT written to Core Director.
 */
import { useEffect, useState } from 'react';
import type { PepConfig } from '@retail-ops/shared-types';
import { useApiClient } from '@/contexts/ApiClientContext';
import type { RaoPerson } from '../../../types';

// Session-level cache — fetch the PEP config once, not per modal open.
let cachedPep: PepConfig | null = null;
let cachedPromise: Promise<PepConfig | null> | null = null;

export interface PepDesignationCardProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  readOnly?: boolean;
}

export function PepDesignationCard({
  person,
  onChange,
  readOnly = false,
}: PepDesignationCardProps): JSX.Element | null {
  const client = useApiClient();
  const [cfg, setCfg] = useState<PepConfig | null>(cachedPep);

  useEffect(() => {
    if (cachedPep) {
      setCfg(cachedPep);
      return;
    }
    let cancelled = false;
    if (!cachedPromise) {
      cachedPromise = client.retail.config
        .pep()
        .then((r) => (r.success && r.data ? r.data : null))
        .catch(() => null)
        .then((c) => {
          if (c) cachedPep = c;
          // On a transient failure (null), clear the cached promise so a later
          // modal open RE-FETCHES — otherwise one config-fetch blip would hide
          // the PEP checkbox for the whole session once Compliance enables it.
          else cachedPromise = null;
          return c;
        });
    }
    void cachedPromise.then((c) => {
      if (!cancelled) setCfg(c);
    });
    return () => {
      cancelled = true;
    };
  }, [client]);

  // Inert until Compliance enables it.
  if (!cfg || !cfg.enabled) return null;

  const checked = !!person.isPep;

  return (
    <div
      style={{
        padding: '12px 14px',
        border: '1px solid var(--ink-200, var(--surface-border))',
        borderRadius: 6,
        background: 'var(--surface-card, #fff)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ flex: 1, fontSize: 13, color: 'var(--pcb-blue-dark)' }}>
          Politically Exposed Person (PEP)
        </strong>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 9px',
            borderRadius: 11,
            background: checked ? 'var(--banner-warn-bg, #fff8e1)' : 'var(--muted-bg, #eef1f4)',
            color: checked ? 'var(--banner-warn-fg, #7a5c00)' : 'var(--ink-600, #5a6672)',
            border: `1px solid ${checked ? 'var(--banner-warn-border, #d4a72c)' : 'var(--muted-bd, #d9dee4)'}`,
          }}
        >
          {checked ? 'Designated' : 'Not designated'}
        </span>
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          color: 'var(--ink-900, #1d1d1f)',
          cursor: readOnly ? 'default' : 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange({ isPep: e.currentTarget.checked })}
          disabled={readOnly}
          style={{ width: 16, height: 16, accentColor: 'var(--pcb-blue, #2968ad)' }}
        />
        Designate this customer as a Politically Exposed Person (PEP)
      </label>

      <details style={{ fontSize: 12 }}>
        <summary
          style={{
            cursor: 'pointer',
            color: 'var(--pcb-blue)',
            fontWeight: 600,
            listStyle: 'none',
          }}
        >
          ⓘ More info — when should I check this box?
        </summary>
        <div
          style={{
            marginTop: 8,
            background: 'var(--info-bg, #f8fbff)',
            border: '1px solid var(--info-bd, #bcd3ec)',
            borderRadius: 6,
            padding: '10px 12px',
            color: 'var(--ink-800, #2a2a2f)',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
          }}
        >
          {cfg.helperText}
        </div>
      </details>
    </div>
  );
}
